import React from 'react'

import ItemPriceAndCTAs from '../common/ItemPriceAndCTAs'
import { DetailItemCardVariant } from '../common/types'
import HotelCtaContent from '../CTAContent/HotelCtaContent'
import { PlannedItemPriceContent } from './PlannedItemPriceContent'

import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import BedbankOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/BedbankOfferPriceDetails'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import offerPageURL from 'lib/offer/offerPageURL'
import { VIEW_OFFER_COPY } from 'tripPlanner/config'
import useAccommodationOfferAvailable from 'tripPlanner/hooks/useAccommodationOfferAvailable'
import { AccommodationItem } from 'tripPlanner/types/tripItem'
import { isLeBookmark } from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { getAccommodationPriceAndLabel } from 'tripPlanner/utils/hotel'
import { findHotelPackage } from 'tripPlanner/utils/offer'

interface Props {
  item: AccommodationItem
  offer: App.HotelOffer | App.BundleOffer | App.BedbankOffer | App.VillaOffer
  viewAsTemplate: boolean
  variant: DetailItemCardVariant
}

function HotelPriceAndCTAs({ item, offer, viewAsTemplate, variant }: Props) {
  let priceAreaContent: JSX.Element | null = null
  let ctaAreaContent: JSX.Element | null = null

  const savedItemData = 'savedItemData' in item ? item.savedItemData : undefined
  const isOfferAvailable = useAccommodationOfferAvailable(
    offer,
    savedItemData,
    offer.id,
  )

  if (item.isSuggested) {
    // We don't currently do suggested hotels. If we ever do, update this.
    return null
  }

  if (viewAsTemplate && isLeBookmark(item)) {
    if (offer.type === 'bedbank_hotel') {
      const { checkIn, checkOut } = item.savedItemData
      priceAreaContent = (
        <BedbankOfferPriceDetails
          offer={offer}
          roomId={item.subCode}
          checkIn={checkIn}
          checkOut={checkOut}
        />
      )
    } else {
      priceAreaContent = (
        <LEOfferPriceDetails
          offer={offer as App.Offer}
          package={findHotelPackage(offer.packages, item)}
        />
      )
    }

    const offerLink = offerPageURL(offer)
    ctaAreaContent = (
      <Group direction="horizontal" gap={12}>
        <SaveTripItemButton item={item} />
        <TextButton kind="primary" to={offerLink} target="_blank">
          {VIEW_OFFER_COPY}
        </TextButton>
      </Group>
    )
  } else {
    let offerLink: string | undefined = undefined

    if (isLeBookmark(item)) {
      const { discountPercent, currencyCode } = item.savedItemData

      const { price, luxPlusPrice, value, priceLabel } =
        getAccommodationPriceAndLabel(offer, item.savedItemData)

      priceAreaContent = (
        <PlannedItemPriceContent
          discountPercent={discountPercent}
          price={price}
          luxPlusPrice={luxPlusPrice}
          value={value}
          priceLabel={priceLabel}
          currencyCode={currencyCode}
          isOfferAvailable={isOfferAvailable}
        />
      )

      offerLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''
    }

    ctaAreaContent = (
      <HotelCtaContent
        offerLink={offerLink}
        item={item}
        isOfferAvailable={isOfferAvailable}
        variant={variant}
        offer={offer}
      />
    )
  }

  return (
    <ItemPriceAndCTAs>
      {priceAreaContent && <div className="price-area">{priceAreaContent}</div>}
      {ctaAreaContent && <div className="cta-area">{ctaAreaContent}</div>}
    </ItemPriceAndCTAs>
  )
}

export default HotelPriceAndCTAs
